import React, { useEffect, useState, useMemo } from "react";
import api, { employeesApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Plus,
  Search,
  Users,
  Mail,
  Phone,
  Pencil,
  Trash2,
  UserCheck,
  UserX,
  Key,
  Shield,
  Check,
  Palette,
  Briefcase,
  Handshake,
  Filter,
  Send,
  Clock,
  CheckCircle,
  GraduationCap,
} from "lucide-react";

// All possible roles
const ALL_ROLES = [
  { value: "all", label: "All Roles" },
  { value: "admin", label: "Admin" },
  { value: "team", label: "Team" },
  { value: "pm", label: "PM" },
  { value: "pm_lead", label: "PM Lead" },
  { value: "sales", label: "Sales" },
  { value: "sales_director", label: "Sales Director" },
  { value: "crm_manager", label: "CRM Manager" },
  { value: "art_director", label: "Art Director" },
  { value: "finance", label: "Finance" },
  { value: "artist", label: "Artist" },
  { value: "client", label: "Client" },
  { value: "partner", label: "Partner" },
  { value: "roleplay_tester", label: "Roleplay Tester" },
];

// Positions
const POSITIONS = [
  { value: "all", label: "All Positions" },
  { value: "project_manager", label: "Project Manager" },
  { value: "sales", label: "Sales Manager" },
  { value: "finance_director", label: "Finance Director" },
  { value: "art_director", label: "Art Director" },
  { value: "3d_artist", label: "3D Artist" },
  { value: "animator", label: "Animator" },
  { value: "designer", label: "Designer" },
  { value: "developer", label: "Developer" },
  { value: "admin", label: "Administrator" },
  { value: "other", label: "Other" },
];

// Portal access options
const PORTAL_ACCESS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "yes", label: "С доступом" },
  { value: "no", label: "Без доступа" },
];

const getRoleBadgeColor = (role) => {
  const colors = {
    admin: "bg-red-500/20 text-red-400",
    team: "bg-blue-500/20 text-blue-400",
    pm: "bg-green-500/20 text-green-400",
    pm_lead: "bg-emerald-500/20 text-emerald-400",
    sales: "bg-yellow-500/20 text-yellow-400",
    sales_director: "bg-orange-500/20 text-orange-400",
    crm_manager: "bg-purple-500/20 text-purple-400",
    art_director: "bg-pink-500/20 text-pink-400",
    finance: "bg-cyan-500/20 text-cyan-400",
    artist: "bg-violet-500/20 text-violet-400",
    client: "bg-amber-500/20 text-amber-400",
    partner: "bg-indigo-500/20 text-indigo-400",
    roleplay_tester: "bg-indigo-500/20 text-indigo-400",
  };
  return colors[role] || "bg-zinc-500/20 text-zinc-400";
};

const InviteCell = ({ user, onResend }) => {
  const [sending, setSending] = React.useState(false);

  if (!user.email) {
    return <span className="text-zinc-600 text-xs">No email</span>;
  }

  // User already has a password — show status
  if (user.has_password) {
    if (user.invite_status?.accepted) {
      return (
        <div className="flex flex-col items-center gap-0.5">
          <span className="flex items-center gap-1 text-emerald-400 text-xs font-medium">
            <CheckCircle className="h-3 w-3" /> Accepted
          </span>
          {user.invite_status.accepted_at && (
            <span className="text-[10px] text-zinc-600">{new Date(user.invite_status.accepted_at).toLocaleDateString()}</span>
          )}
        </div>
      );
    }
    return <span className="text-zinc-500 text-xs">-</span>;
  }

  // No password — show invite status
  if (user.invite_status?.sent_at) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="flex items-center gap-1 text-amber-400 text-xs font-medium">
          <Clock className="h-3 w-3" /> Pending
        </span>
        <span className="text-[10px] text-zinc-600">{new Date(user.invite_status.sent_at).toLocaleDateString()}</span>
        <button
          onClick={async () => { setSending(true); await onResend(user.id, user.name); setSending(false); }}
          disabled={sending}
          className="text-[10px] text-orange-400 hover:text-orange-300"
        >
          {sending ? "..." : "Resend"}
        </button>
      </div>
    );
  }

  // No password, no invite sent
  return (
    <button
      data-testid={`send-invite-${user.id}`}
      onClick={async () => { setSending(true); await onResend(user.id, user.name); setSending(false); }}
      disabled={sending}
      className="flex items-center gap-1 px-2 py-1 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 rounded text-xs font-medium transition-colors mx-auto"
    >
      <Send className="h-3 w-3" />
      {sending ? "..." : "Invite"}
    </button>
  );
};

const UsersPage = () => {
  const { formatAmount } = useCurrency();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("team");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("all");
  const [portalAccessFilter, setPortalAccessFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Password reset
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  
  // Role change
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState("");

  // Add user (creates a new portal account + sends an invite email)
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", role: "partner", roleplay_access: false, set_password: false, password: "" });
  const [addingUser, setAddingUser] = useState(false);
  const MANAGE_ROLEPLAY_ROLES = ["admin", "sales", "sales_director", "crm_manager", "pm_lead", "roleplay_tester"];

  // Sent invites overview (see what went out + copy setup link when email is down)
  const [invitesDialogOpen, setInvitesDialogOpen] = useState(false);
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  
  // Tab counts
  const [tabCounts, setTabCounts] = useState({ team: 0, artists: 0, clients: 0, partners: 0 });

  useEffect(() => {
    fetchUsers();
    fetchTabCounts();
  }, [activeTab, showInactive]);
  
  const fetchTabCounts = async () => {
    try {
      const params = showInactive ? {} : { is_active: true };
      const [team, artists, clients, partners] = await Promise.all([
        api.get("/all-users", { params: { ...params, category: "team" } }),
        api.get("/all-users", { params: { ...params, category: "artists" } }),
        api.get("/all-users", { params: { ...params, category: "clients" } }),
        api.get("/all-users", { params: { ...params, category: "partners" } }),
      ]);
      setTabCounts({
        team: team.data.length,
        artists: artists.data.length,
        clients: clients.data.length,
        partners: partners.data.length,
      });
    } catch (error) {
      console.error("Error fetching tab counts:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { category: activeTab };
      if (!showInactive) params.is_active = true;
      
      const response = await api.get("/all-users", { params });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error loading users");
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search
      if (searchQuery && !user.name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !user.email?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Role filter
      if (roleFilter !== "all" && user.role !== roleFilter) return false;
      // Position filter
      if (positionFilter !== "all" && user.position !== positionFilter) return false;
      // Portal access filter
      if (portalAccessFilter === "yes" && !user.has_portal_access) return false;
      if (portalAccessFilter === "no" && user.has_portal_access) return false;
      
      return true;
    });
  }, [users, searchQuery, roleFilter, positionFilter, portalAccessFilter]);

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };
  
  const handleResendInvite = async (userId, userName) => {
    try {
      await api.post(`/admin/resend-invite/${userId}`);
      toast.success(`Invite sent to ${userName}`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    }
  };

  const openInvitesDialog = async () => {
    setInvitesDialogOpen(true);
    setInvitesLoading(true);
    try {
      const res = await employeesApi.listInvites();
      setInvites(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invites");
    } finally {
      setInvitesLoading(false);
    }
  };

  // Build the setup link from the current origin so it works regardless of
  // whether the backend has APP_URL configured. Only valid for pending invites.
  const inviteLink = (token) => `${window.location.origin}/setup-account?token=${token}`;

  const copyInviteLink = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteLink(token));
      toast.success("Setup link copied — send it to the user");
    } catch (e) {
      // Clipboard API can be blocked (non-HTTPS / permissions) — surface the link.
      window.prompt("Copy this setup link:", inviteLink(token));
    }
  };

  // Expired/invalidated invites have no usable link — mint a fresh token and copy
  // it straight away, no email needed. Then refresh the list to show the new state.
  const [renewingId, setRenewingId] = useState(null);
  const renewInviteLink = async (userId) => {
    setRenewingId(userId);
    try {
      const res = await employeesApi.regenerateInvite(userId);
      await copyInviteLink(res.data.token);
      const list = await employeesApi.listInvites();
      setInvites(list.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to renew invite");
    } finally {
      setRenewingId(null);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/employees/${userToDelete.id}/permanent`);
      toast.success(`${userToDelete.name} deleted`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error("Delete error: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetPassword = async () => {
    if (!editingUser || !newPassword) return;
    try {
      await employeesApi.resetPassword(editingUser.id, { new_password: newPassword });
      toast.success("Password changed");
      setPasswordDialogOpen(false);
      setNewPassword("");
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleRoleChange = async () => {
    if (!editingUser || !newRole) return;
    try {
      await employeesApi.updateRole(editingUser.id, { role: newRole });
      toast.success("Role updated");
      setRoleDialogOpen(false);
      setNewRole("");
      fetchUsers();
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    }
  };

  const openAddDialog = () => {
    // Default the new account's role to whatever tab we're on (Partners → partner).
    const tabRole = { team: "team", artists: "artist", clients: "client", partners: "partner" }[activeTab] || "team";
    setAddForm({ name: "", email: "", role: tabRole, roleplay_access: activeTab === "partners", set_password: false, password: "" });
    setAddDialogOpen(true);
  };

  const handleAddUser = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    // When setting a password directly, enforce the backend's 6-char minimum
    // up front so the account isn't created without the password silently.
    const withPassword = addForm.set_password && addForm.password.trim().length > 0;
    if (addForm.set_password && addForm.password.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setAddingUser(true);
    try {
      const res = await employeesApi.createPortalUser({
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        role: addForm.role,
        ...(withPassword ? { password: addForm.password.trim() } : {}),
      });
      const newId = res.data?.user?.id;
      // Roleplay access is an additive flag — only meaningful for roles that
      // don't already include the trainer. Set it after creation if requested.
      if (newId && addForm.roleplay_access && !MANAGE_ROLEPLAY_ROLES.includes(addForm.role)) {
        await employeesApi.setRoleplayAccess(newId, { enabled: true });
      }
      toast.success(withPassword
        ? "User created with a password — share the login manually"
        : (res.data?.invite_sent ? "User created — invite email sent" : "User created"));
      setAddDialogOpen(false);
      fetchUsers();
      fetchTabCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setAddingUser(false);
    }
  };

  const handleToggleRoleplay = async (enabled) => {
    if (!editingUser) return;
    try {
      await employeesApi.setRoleplayAccess(editingUser.id, { enabled });
      toast.success(enabled ? "Roleplay access enabled" : "Roleplay access disabled");
      setEditingUser({ ...editingUser, roleplay_access: enabled });
      fetchUsers();
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreatePortalAccess = async (user) => {
    const password = prompt("Enter password for new account:");
    if (!password) return;
    
    try {
      await employeesApi.createAccount(user.id, {
        password,
        role: user.role || "team"
      });
      toast.success("Portal access created");
      fetchUsers();
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    }
  };

  // Get available roles for current tab
  const getTabRoles = () => {
    switch (activeTab) {
      case "team":
        return ALL_ROLES.filter(r => ["all", "admin", "team", "pm", "pm_lead", "sales", "sales_director", "crm_manager", "art_director", "finance"].includes(r.value));
      case "artists":
        return [{ value: "all", label: "All" }, { value: "artist", label: "Artist" }];
      case "clients":
        return [{ value: "all", label: "All" }, { value: "client", label: "Client" }];
      case "partners":
        return [{ value: "all", label: "All" }, { value: "partner", label: "Partner" }];
      default:
        return ALL_ROLES;
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-50">Users</h1>
          <p className="text-zinc-400 mt-1">Manage all system users</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openInvitesDialog} variant="outline" className="border-zinc-700 hover:bg-zinc-800" data-testid="invites-btn">
            <Send className="h-4 w-4 mr-2" />
            Invites
          </Button>
          <Button onClick={openAddDialog} className="bg-amber-600 hover:bg-amber-700" data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setRoleFilter("all"); setPositionFilter("all"); }}>
        <TabsList className="bg-zinc-800/50 border border-zinc-700/50">
          <TabsTrigger value="team" className="data-[state=active]:bg-zinc-700 gap-2">
            <Briefcase className="h-4 w-4" />
            Team
            <Badge variant="secondary" className="ml-1 bg-zinc-700">{tabCounts.team}</Badge>
          </TabsTrigger>
          <TabsTrigger value="artists" className="data-[state=active]:bg-zinc-700 gap-2">
            <Palette className="h-4 w-4" />
            Artists
            <Badge variant="secondary" className="ml-1 bg-zinc-700">{tabCounts.artists}</Badge>
          </TabsTrigger>
          <TabsTrigger value="clients" className="data-[state=active]:bg-zinc-700 gap-2">
            <Users className="h-4 w-4" />
            Clients
            <Badge variant="secondary" className="ml-1 bg-zinc-700">{tabCounts.clients}</Badge>
          </TabsTrigger>
          <TabsTrigger value="partners" className="data-[state=active]:bg-zinc-700 gap-2">
            <Handshake className="h-4 w-4" />
            Partners
            <Badge variant="secondary" className="ml-1 bg-zinc-700">{tabCounts.partners}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="mt-4 bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-zinc-800 border-zinc-700"
                  data-testid="users-search"
                />
              </div>

              {/* Role filter */}
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700">
                  <Shield className="h-4 w-4 mr-2 text-zinc-500" />
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {getTabRoles().map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Position filter - only for team */}
              {activeTab === "team" && (
                <Select value={positionFilter} onValueChange={setPositionFilter}>
                  <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                    <Briefcase className="h-4 w-4 mr-2 text-zinc-500" />
                    <SelectValue placeholder="Position" />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map((pos) => (
                      <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Portal access filter */}
              <Select value={portalAccessFilter} onValueChange={setPortalAccessFilter}>
                <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700">
                  <Key className="h-4 w-4 mr-2 text-zinc-500" />
                  <SelectValue placeholder="Доступ" />
                </SelectTrigger>
                <SelectContent>
                  {PORTAL_ACCESS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Show inactive */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  id="show-inactive"
                />
                <Label htmlFor="show-inactive" className="text-zinc-400 text-sm">
                  Inactive
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content for all tabs */}
        <TabsContent value={activeTab} className="mt-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No users found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400 w-12">#</TableHead>
                      <TableHead className="text-zinc-400">Name</TableHead>
                      <TableHead className="text-zinc-400">Email</TableHead>
                      <TableHead className="text-zinc-400">Role</TableHead>
                      {activeTab === "team" && (
                        <TableHead className="text-zinc-400">Position</TableHead>
                      )}
                      <TableHead className="text-zinc-400 text-center">Portal</TableHead>
                      <TableHead className="text-zinc-400 text-center">Invite</TableHead>
                      <TableHead className="text-zinc-400 text-center">Status</TableHead>
                      <TableHead className="text-zinc-400 w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user, index) => (
                      <TableRow 
                        key={user.id} 
                        className="border-zinc-800 hover:bg-zinc-800/50"
                        data-testid={`user-row-${user.id}`}
                      >
                        <TableCell className="text-zinc-500 font-mono text-sm">
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-zinc-50">{user.name}</div>
                          {user.phone && (
                            <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                              <Phone className="h-3 w-3" />
                              {user.phone}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-zinc-400">
                            <Mail className="h-4 w-4 text-zinc-600" />
                            {user.email || "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("font-medium", getRoleBadgeColor(user.role))}>
                            {user.role || "-"}
                          </Badge>
                        </TableCell>
                        {activeTab === "team" && (
                          <TableCell className="text-zinc-400">
                            {user.position || "-"}
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          {user.has_portal_access ? (
                            <Badge className="bg-green-500/20 text-green-400">
                              <Check className="h-3 w-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-zinc-500 hover:text-amber-400"
                              onClick={() => handleCreatePortalAccess(user)}
                              data-testid={`create-access-${user.id}`}
                            >
                              <Key className="h-4 w-4 mr-1" />
                              Create
                            </Button>
                          )}
                        </TableCell>
                        {/* Invite column */}
                        <TableCell className="text-center">
                          <InviteCell user={user} onResend={handleResendInvite} />
                        </TableCell>
                        <TableCell className="text-center">
                          {user.is_active !== false ? (
                            <Badge className="bg-green-500/20 text-green-400">
                              <UserCheck className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">
                              <UserX className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-zinc-400 hover:text-amber-400"
                              onClick={() => handleEditClick(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-zinc-400 hover:text-red-400"
                              onClick={() => handleDeleteClick(user)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add User Dialog — creates a new portal account + sends an invite email */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400">Name</Label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="Full name"
                data-testid="add-user-name"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Email</Label>
              <Input
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="name@company.com"
                data-testid="add-user-email"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Role</Label>
              <Select
                value={addForm.role}
                onValueChange={(v) => setAddForm({ ...addForm, role: v, roleplay_access: MANAGE_ROLEPLAY_ROLES.includes(v) ? false : addForm.roleplay_access })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_ROLES.filter((r) => r.value !== "all").map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Additive Roleplay access — only for roles that don't already have
                the trainer (manage roles + roleplay_tester get it via the role). */}
            {!MANAGE_ROLEPLAY_ROLES.includes(addForm.role) && (
              <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-zinc-800/50">
                <input
                  type="checkbox"
                  checked={!!addForm.roleplay_access}
                  onChange={(e) => setAddForm({ ...addForm, roleplay_access: e.target.checked })}
                  className="rounded border-zinc-600 bg-zinc-800 text-indigo-500 focus:ring-indigo-500"
                  data-testid="add-user-roleplay"
                />
                <GraduationCap className="h-4 w-4 text-indigo-400" />
                <span className="text-zinc-300 text-sm">Roleplay trainer access</span>
              </label>
            )}
            {/* Set a password directly instead of relying on the invite email —
                useful when email delivery is down or you want to hand over the
                login by hand. Backend requires min 6 chars. */}
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-zinc-800/50">
                <input
                  type="checkbox"
                  checked={!!addForm.set_password}
                  onChange={(e) => setAddForm({ ...addForm, set_password: e.target.checked, password: e.target.checked ? addForm.password : "" })}
                  className="rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                  data-testid="add-user-set-password"
                />
                <Key className="h-4 w-4 text-amber-400" />
                <span className="text-zinc-300 text-sm">Set password now (skip email invite)</span>
              </label>
              {addForm.set_password && (
                <Input
                  type="text"
                  value={addForm.password}
                  onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Password (min 6 chars) — share it manually"
                  data-testid="add-user-password"
                />
              )}
            </div>
            <Button
              className="w-full bg-amber-600 hover:bg-amber-700"
              onClick={handleAddUser}
              disabled={addingUser || !addForm.name.trim() || !addForm.email.trim() || (addForm.set_password && addForm.password.trim().length < 6)}
              data-testid="add-user-submit"
            >
              {addingUser ? "Creating..." : (addForm.set_password ? "Create with Password" : "Create & Send Invite")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sent Invites Dialog — overview of every issued invite: acceptance +
          email delivery status, plus a copyable setup link for pending ones. */}
      <Dialog open={invitesDialogOpen} onOpenChange={setInvitesDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Sent Invites</DialogTitle>
          </DialogHeader>
          <div className="mt-2 max-h-[60vh] overflow-y-auto">
            {invitesLoading ? (
              <p className="text-zinc-500 text-sm py-8 text-center">Loading…</p>
            ) : invites.length === 0 ? (
              <p className="text-zinc-500 text-sm py-8 text-center">No invites issued yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">User</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400">Email delivery</TableHead>
                    <TableHead className="text-zinc-400">Sent</TableHead>
                    <TableHead className="text-zinc-400 text-right">Link</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((inv) => {
                    const stateColor = {
                      accepted: "bg-emerald-500/20 text-emerald-400",
                      pending: "bg-amber-500/20 text-amber-400",
                      expired: "bg-red-500/20 text-red-400",
                      superseded: "bg-zinc-600/30 text-zinc-400",
                    }[inv.state] || "bg-zinc-600/30 text-zinc-400";
                    const deliveryColor = {
                      sent: "text-emerald-400",
                      skipped: "text-amber-400",
                      failed: "text-red-400",
                    }[inv.email_status] || "text-zinc-500";
                    return (
                      <TableRow key={inv.token} className="border-zinc-800">
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-zinc-100">{inv.name || "—"}</span>
                            <span className="text-xs text-zinc-500">{inv.email}</span>
                            <span className="text-[10px] text-zinc-600">{inv.role}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("font-medium capitalize", stateColor)}>{inv.state}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className={cn("text-xs font-medium capitalize", deliveryColor)}>{inv.email_status}</span>
                          {inv.email_status === "skipped" && (
                            <div className="text-[10px] text-zinc-600">email not configured</div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-zinc-500">
                          {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {inv.state === "pending" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-zinc-700 hover:bg-zinc-800 h-7 text-xs"
                              onClick={() => copyInviteLink(inv.token)}
                              data-testid={`copy-invite-${inv.user_id}`}
                            >
                              Copy link
                            </Button>
                          ) : (inv.state === "expired" || inv.state === "superseded") ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-700/60 text-orange-400 hover:bg-orange-500/10 h-7 text-xs"
                              onClick={() => renewInviteLink(inv.user_id)}
                              disabled={renewingId === inv.user_id}
                              data-testid={`renew-invite-${inv.user_id}`}
                            >
                              {renewingId === inv.user_id ? "…" : "Renew link"}
                            </Button>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              Edit: {editingUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4 mt-4">
              {/* User info */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <span className="text-zinc-400">Email</span>
                  <span className="text-zinc-50">{editingUser.email || "-"}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <span className="text-zinc-400">Role</span>
                  <Badge className={cn("font-medium", getRoleBadgeColor(editingUser.role))}>
                    {editingUser.role}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <span className="text-zinc-400">Доступ к порталу</span>
                  <span className={editingUser.has_portal_access ? "text-green-400" : "text-zinc-500"}>
                    {editingUser.has_portal_access ? "Yes" : "No"}
                  </span>
                </div>
              </div>

              {/* Actions */}
              {editingUser.has_portal_access && (
                <div className="space-y-2 pt-4 border-t border-zinc-800">
                  <Button
                    variant="outline"
                    className="w-full justify-start border-zinc-700 hover:bg-zinc-800"
                    onClick={() => { setPasswordDialogOpen(true); }}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Reset Password
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start border-zinc-700 hover:bg-zinc-800"
                    onClick={() => { setNewRole(editingUser.role); setRoleDialogOpen(true); }}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Change Role
                  </Button>
                  {/* Additive Roleplay access — only for roles that don't already
                      have the sales trainer (manage roles + roleplay_tester get it
                      via their role). Marker: roleplay-access-flag-v1 */}
                  {!["admin", "sales", "sales_director", "crm_manager", "pm_lead", "roleplay_tester"].includes(editingUser.role) && (
                    <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-zinc-800/50">
                      <input
                        type="checkbox"
                        checked={!!editingUser.roleplay_access}
                        onChange={(e) => handleToggleRoleplay(e.target.checked)}
                        className="rounded border-zinc-600 bg-zinc-800 text-indigo-500 focus:ring-indigo-500"
                        data-testid="roleplay-access-toggle"
                      />
                      <GraduationCap className="h-4 w-4 text-indigo-400" />
                      <span className="text-zinc-300 text-sm">Roleplay trainer access</span>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700"
                placeholder="Enter new password"
              />
            </div>
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700"
              onClick={handleResetPassword}
              disabled={!newPassword}
            >
              Save Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Change Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400">New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_ROLES.filter(r => r.value !== "all").map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700"
              onClick={handleRoleChange}
              disabled={!newRole}
            >
              Save Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Delete User?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete <span className="text-zinc-50 font-medium">{userToDelete?.name}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-50 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
